/**
 * Runtime environment flags + the Firebase client config for the portal.
 *
 * Emulator mode is on when `?emu=1` is in the URL OR this is a Vite dev build
 * (`import.meta.env.DEV`) — mirrors the pong-web convention (CREATOR-PLATFORM.md:
 * "hardcode with emulator override via ?emu=1/DEV like pong-web does"). Local
 * dev + tests never touch the live `djgg-platform` project; they point at the
 * `demo-djgg` emulator suite.
 *
 * The two Firebase client configs are NOT defined here — they are the ONE copy
 * every djgg web tier shares (`@djgg/cloud`'s `webConfig.ts`, where the public
 * apiKey and the `authDomain` choice are documented). The portal only decides
 * WHICH of them to boot with, and re-exports them for its own consumers.
 */
import {
  EMULATOR_FIREBASE_CONFIG,
  LIVE_FIREBASE_CONFIG,
  type EmulatorConfig,
  type FirebaseClientConfig,
} from '@djgg/cloud';

export { EMULATOR_FIREBASE_CONFIG, LIVE_FIREBASE_CONFIG };

/** Whether to run against the Firebase emulator suite. */
export function useEmulators(): boolean {
  const forced =
    typeof location !== 'undefined' &&
    new URLSearchParams(location.search).get('emu') === '1';
  return forced || Boolean(import.meta.env.DEV);
}

/** Emulator wiring for local dev (auth/firestore/rtdb on localhost). */
export const PORTAL_EMULATORS: EmulatorConfig = {
  host: 'localhost',
  ports: { auth: 9099, firestore: 8080, database: 9000 },
};

/**
 * The Cloud Functions emulator port. Kept SEPARATE from `PORTAL_EMULATORS.ports`
 * because @djgg/cloud's `EmulatorConfig` (which drives `createFirebaseApp`) wires
 * only auth/firestore/rtdb — functions is a portal concern (the portal owns the
 * callable seam), so `createFirebaseServices` calls `connectFunctionsEmulator`
 * with this port itself. Matches the firebase.json emulator default (5001).
 */
export const PORTAL_FUNCTIONS_EMULATOR_PORT = 5001;

/** The config to boot with, chosen by {@link useEmulators}. */
export function firebaseConfig(): FirebaseClientConfig {
  return useEmulators() ? EMULATOR_FIREBASE_CONFIG : LIVE_FIREBASE_CONFIG;
}

/** Region the callable Cloud Functions are deployed to. */
export const FUNCTIONS_REGION = 'us-central1';

/** Portal app version (surfaced in the footer / debug). */
export const APP_VERSION = '0.79.1';

/** Exact immutable release identity injected by the Hosting candidate build. */
declare const __DJGG_RELEASE_SHA__: string;
export const RELEASE_SHA =
  typeof __DJGG_RELEASE_SHA__ === 'string' ? __DJGG_RELEASE_SHA__ : 'dev';

/** Supabase project constants (PUBLIC by design — the publishable key is the
 *  browser key and RLS is the gate; see plan 002). The portal's PROD data
 *  backend after the Postgres cutover. */
export const SUPABASE_URL = 'https://mooxxgusfwnowokqjhst.supabase.co';
export const SUPABASE_PUBLISHABLE_KEY = 'sb_publishable_Yr-2DZr7NVwnUOgsE2ldLg_AH4lIEww';
